import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'algoliaTableColumns';

interface UseTableColumnsProps {
  propSelectedColumns: string[];
  onTagColumnsChange?: (columns: string[]) => void;
}

/**
 * Custom hook for managing table columns with localStorage persistence
 */
const useTableColumns = ({
  propSelectedColumns,
  onTagColumnsChange,
}: UseTableColumnsProps) => {
  // Initialize selected columns from localStorage or props
  const getInitialColumns = useCallback((): string[] => {
    if (typeof window === 'undefined') {
      return propSelectedColumns;
    }

    try {
      const savedColumns = localStorage.getItem(STORAGE_KEY);
      if (savedColumns) {
        return JSON.parse(savedColumns);
      }
    } catch (e) {
      // Silently fallback to default columns
    }
    return propSelectedColumns;
  }, [propSelectedColumns]);

  // Use local state to track selected columns
  const [selectedTagColumns, setSelectedTagColumns] = useState<string[]>(
    getInitialColumns(),
  );

  // When prop columns change, update our local state
  useEffect(() => {
    if (
      propSelectedColumns.length > 0 &&
      JSON.stringify(propSelectedColumns) !== JSON.stringify(selectedTagColumns)
    ) {
      setSelectedTagColumns(propSelectedColumns);
    }
  }, [propSelectedColumns, selectedTagColumns]);

  // When columns change, we need to save them to localStorage
  const handleColumnsChange = useCallback(
    (columns: string[]) => {
      // Save to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
      } catch (error) {
        // Continue execution even if localStorage fails
      }

      // Update local state
      setSelectedTagColumns(columns);

      // Call the callback
      if (onTagColumnsChange) {
        onTagColumnsChange(columns);
      }
    },
    [onTagColumnsChange],
  );

  return {
    selectedTagColumns,
    handleColumnsChange,
  };
};

export default useTableColumns;
